import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lightbulb, Users } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { api } from "@/api";
import { Reveal, Overline } from "@/components/motion";

const EMPTY = {
  title: "", description: "", objective: "", fund_source: "Donation",
  fund_distribution: "", contact_name: "", contact_email: "", contact_phone: "",
};

const SuggestCampaign = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/suggestions", form);
      setDone(true);
      toast.success(t("suggest.success"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-forest/20 rounded-full px-5 py-3.5 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300";
  const areaCls = "w-full bg-transparent border border-forest/20 rounded-3xl px-5 py-4 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:border-clay transition-colors duration-300 resize-none";
  const sources = [
    { value: "Donation", label: t("suggest.srcDonation") },
    { value: "Product sales", label: t("suggest.srcProduct") },
    { value: "Service sales", label: t("suggest.srcService") },
    { value: "Other", label: t("suggest.srcOther") },
  ];

  return (
    <div data-testid="suggest-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-16 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{t("suggest.overline")}</Overline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-forest" data-testid="suggest-title">
              {t("suggest.title")}
            </h1>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-sage max-w-md">{t("suggest.sub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 flex items-start gap-4">
              <Lightbulb size={20} className="text-clay mt-1" />
              <p className="text-sm leading-relaxed text-sage max-w-sm">{t("suggest.note")}</p>
            </div>
            <div className="mt-8 flex items-start gap-4 border border-clay/30 bg-clay/5 p-6 max-w-md" data-testid="suggest-member-required">
              <Users size={20} className="text-clay mt-1 shrink-0" />
              <div>
                <p className="text-sm leading-relaxed text-forest">{t("suggest.memberRequired")}</p>
                <Link to="/membership" data-testid="suggest-membership-link" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-clay hover:text-forest transition-colors duration-300">
                  {t("suggest.memberRequiredLink")} <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            {done ? (
              <div className="border border-moss/30 bg-moss/5 p-12 text-center" data-testid="suggest-success">
                <p className="font-serif text-3xl text-forest">{t("suggest.successTitle")}</p>
                <p className="mt-4 text-sage">{t("suggest.success")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-forest/15 bg-sand/40 p-8 md:p-12 flex flex-col gap-5" data-testid="suggest-form">
                <input data-testid="suggest-title-input" required value={form.title} onChange={set("title")} placeholder={t("suggest.campaignTitle")} className={inputCls} />
                <textarea data-testid="suggest-description-input" required rows={6} value={form.description} onChange={set("description")} placeholder={t("suggest.description")} className={areaCls} />
                <input data-testid="suggest-objective-input" required value={form.objective} onChange={set("objective")} placeholder={t("suggest.objective")} className={inputCls} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-sage mb-2 pl-1">{t("suggest.fundSource")}</label>
                    <select data-testid="suggest-fund-source" value={form.fund_source} onChange={set("fund_source")} className="w-full bg-transparent border border-forest/20 rounded-full px-4 py-3.5 text-sm text-forest focus:outline-none focus:border-clay transition-colors duration-300">
                      {sources.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-sage mb-2 pl-1">{t("suggest.fundDistribution")}</label>
                    <input data-testid="suggest-fund-distribution-input" required value={form.fund_distribution} onChange={set("fund_distribution")} placeholder={t("suggest.fundDistributionPh")} className={inputCls} />
                  </div>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mt-4">{t("suggest.contactSection")}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <input data-testid="suggest-contact-name-input" required value={form.contact_name} onChange={set("contact_name")} placeholder={t("suggest.contactName")} className={inputCls} />
                  <input data-testid="suggest-contact-email-input" required type="email" value={form.contact_email} onChange={set("contact_email")} placeholder={t("suggest.contactEmail")} className={inputCls} />
                  <input data-testid="suggest-contact-phone-input" required type="tel" value={form.contact_phone} onChange={set("contact_phone")} placeholder={t("suggest.contactPhone")} className={inputCls} />
                </div>
                <button
                  data-testid="suggest-submit"
                  disabled={loading}
                  className="mt-2 inline-flex w-fit items-center gap-2 bg-forest text-cream px-10 py-4 rounded-full font-medium hover:bg-clay hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
                >
                  {t("suggest.submit")} <ArrowUpRight size={18} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default SuggestCampaign;
